/**
 * Acto médico del episodio (2026-08-25): el médico registra su
 * tratamiento o cirugía DESDE el evolutivo, ligado al episodio y con su
 * fecha — así lleva su propio registro de actos médicos sin salir de la
 * historia, y facturación lo ve en Colaboradores.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Banknote, Plus } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import ColabActividadDialog from '../ColabActividadDialog';

const eur = (v) => (v === null || v === undefined ? '—'
  : Number(v).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €');

const ROLES_GESTION = ['admin', 'direccion', 'administrativo'];

export default function ActoMedicoEpisodio({ episodio, patient }) {
  const { user } = useAuth();
  const rol = (user?.role || '').toLowerCase();
  const esGestion = ROLES_GESTION.includes(rol);
  const puedeVer = esGestion || rol === 'doctor';
  const [actos, setActos] = useState(null);
  const [esDiaQx, setEsDiaQx] = useState(false);
  const [catalogos, setCatalogos] = useState(null);
  const [dialogo, setDialogo] = useState(null);   // 'tratamiento' | 'cirugia'

  const cargar = useCallback(() => {
    if (!puedeVer || !episodio?.id) return;
    api.get('/api/colaboradores/actividades', { params: { episodio_id: episodio.id } })
      .then((r) => { setActos(r.data?.items || []); setEsDiaQx(!!r.data?.es_dia_quirofano); })
      .catch(() => setActos([]));
  }, [puedeVer, episodio?.id]);

  useEffect(() => { cargar(); }, [cargar]);

  if (!puedeVer) return null;

  const abrir = (tipo) => {
    if (!catalogos) {
      api.get('/api/colaboradores/catalogos').then((r) => setCatalogos(r.data)).catch(() => {});
    }
    setDialogo(tipo);
  };

  const nombrePaciente = [patient?.name, patient?.surname1, patient?.surname2]
    .filter(Boolean).join(' ').trim();

  return (
    <div className="mt-3 border-t border-dashed border-border pt-2.5" data-testid="acto-medico-episodio">
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <Banknote className="w-3.5 h-3.5 text-[#2E6E5E]" /> Acto médico (facturación)
        </span>
        <button type="button" onClick={() => abrir('tratamiento')}
          className="inline-flex items-center gap-1 text-xs rounded-md border border-[#2E6E5E]/40 text-[#1E4A3F] px-2 py-1 hover:bg-[rgba(46,110,94,0.06)]"
          data-testid="acto-medico-tratamiento">
          <Plus className="w-3 h-3" /> Tratamiento
        </button>
        {(esGestion || (user?.colab_cirugias && esDiaQx)) && (
          <button type="button" onClick={() => abrir('cirugia')}
            className="inline-flex items-center gap-1 text-xs rounded-md border border-border text-muted-foreground px-2 py-1 hover:border-[#2E6E5E]/40 hover:text-[#1E4A3F]"
            data-testid="acto-medico-cirugia">
            <Plus className="w-3 h-3" /> Cirugía
          </button>
        )}
      </div>
      {actos && actos.length === 0 && (
        <p className="mt-1 text-[11px] text-amber-700">
          Obligatorio antes de cerrar: registra el acto o marca «revisión sin tratamiento».
        </p>
      )}
      {actos && actos.length > 0 && (
        <ul className="mt-1.5 space-y-1">
          {actos.map((a) => (
            <li key={a.id} className="text-xs text-[#13231E] flex items-center gap-2" data-testid={`acto-${a.id}`}>
              <span className={`inline-block w-1.5 h-1.5 rounded-full ${a.pagado ? 'bg-[#2E6E5E]' : 'bg-amber-500'}`} />
              <span className="min-w-0 truncate">
                {a.tipo === 'tratamiento'
                  ? `${a.tratamiento}${a.productos?.length ? ` · ${a.productos.map((l) => `${l.producto}${l.cantidad > 1 ? ` ×${l.cantidad}` : ''}`).join(' + ')}` : a.producto ? ` · ${a.producto}${a.cantidad > 1 ? ` ×${a.cantidad}` : ''}` : ''}`
                  : `${a.intervencion} (cirugía)`}
              </span>
              <span className="ml-auto tabular-nums font-medium shrink-0">{eur(a.parte_medico)}</span>
            </li>
          ))}
        </ul>
      )}
      <ColabActividadDialog
        tipo={dialogo}
        onClose={() => setDialogo(null)}
        catalogos={catalogos}
        medicos={[]}
        esGestion={false}
        medicoActual={null}
        fijo={{
          paciente_nombre: nombrePaciente || patient?.name || '',
          patient_id: patient?.id,
          fecha: (episodio?.date || episodio?.created_at || '').slice(0, 10),
          episodio_id: episodio?.id,
        }}
        onCreada={() => { setDialogo(null); cargar(); }}
      />
    </div>
  );
}
